import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatMoeda, formatDataSP, formatDataHoraSP } from '@fiodecobre/shared';
import { SaldoInsuficienteModal, lerSaldoInsuficiente, type SaldoInsuficiente } from '../caixa/SaldoInsuficienteModal';

interface FormaPagamento { id: number; nome: string; tipo: string }
// `eh_cofre` vem carimbado pelo servidor (mesmo predicado `SQL_EH_COFRE` que decide a inclusao do
// Cofre na lista) — nao se testa o nome aqui. Ver [DA-427] e o comentario da rota.
interface Caixa { id: number; nome: string; tipo: string; eh_cofre?: boolean }

// Pagamento que a loja fez pela conta Mercado Pago (aba Conciliacao Pgto, migration 0341).
export interface MpPagamentoEnviado {
  id: number;
  mp_payment_id: string;
  valor: string;
  metodo: string | null;
  descricao: string | null;
  destinatario: string | null;
  data_pagamento: string | null;
  origem?: 'consulta' | 'email' | 'relatorio';
  nome_pelo_email?: boolean;
  // Conta de quem recebeu o Pix e o fornecedor dono dela (migration 0344) — so na aba Conciliacao Pgto.
  conta_destino?: string | null;
  fornecedor_conta?: string | null;
  total_vinculado: string;
  titulos: Array<{ id: number; numero_documento: string | null; descricao: string; valor_pago: string; fornecedor: string | null; automatico?: boolean }>;
}

// Boleto pago com saldo vem do MP como `account_money`, com o banco emissor na descricao. O que so o
// RELATORIO do MP trouxe nao tem nome nem banco: mostra o numero da transacao, o mesmo do comprovante.
export function rotuloMpPagamento(p: MpPagamentoEnviado): { tipo: string; detalhe: string } {
  const transacao = /^\d+$/.test(p.mp_payment_id) ? `transação ${p.mp_payment_id}` : '';
  if (p.metodo === 'pix') return { tipo: 'Pix enviado', detalhe: p.destinatario || transacao };
  return { tipo: 'Boleto / conta', detalhe: p.descricao || transacao };
}

const ehContaMercadoPago = (nome: string | undefined) => !!nome && /mercado\s*pago/i.test(nome);

const KEY_LAST_FP = 'contas_pagar.last_fp';
const KEY_LAST_CAIXA = 'contas_pagar.last_caixa';

export function ModalBaixarContaPagar({ conta, onFechar, onBaixado, mpPagamentoInicial = null }: {
  conta: any;
  onFechar: () => void;
  onBaixado: () => void;
  // Veio do botao Vincular da aba Conciliacao Pgto: o pagamento ja foi escolhido LA, pelo gesto dele.
  mpPagamentoInicial?: MpPagamentoEnviado | null;
}) {
  const toast = useToast();
  const [formas, setFormas] = useState<FormaPagamento[]>([]);
  const [forma, setForma] = useState<string>('');
  const [caixas, setCaixas] = useState<Caixa[]>([]);
  const [caixa, setCaixa] = useState<string>('');
  const [carregandoCaixas, setCarregandoCaixas] = useState(false);
  const [valor, setValor] = useState<number>(Number(conta.valor));
  const [observacoes, setObservacoes] = useState('');
  // Pagamento recusado pela trava anti-negativo do caixa — modal, nao toast.
  const [saldoInsuf, setSaldoInsuf] = useState<SaldoInsuficiente | null>(null);
  const [salvando, setSalvando] = useState(false);
  // Conta Mercado Pago: a baixa pede o pagamento do MP, ou a declaracao de que ele nao aparece
  // (o servidor recusa sem um dos dois). Nada vem marcado — so o que ele escolheu na aba.
  const [pagamentosMp, setPagamentosMp] = useState<MpPagamentoEnviado[]>(mpPagamentoInicial ? [mpPagamentoInicial] : []);
  const [mpSel, setMpSel] = useState<number | null>(mpPagamentoInicial?.id ?? null);
  const [semVinculo, setSemVinculo] = useState(false);
  const [carregandoMp, setCarregandoMp] = useState(false);
  const ehContaMp = ehContaMercadoPago(caixas.find((c) => String(c.id) === caixa)?.nome);
  const faltaVinculoMp = ehContaMp && !mpSel && !semVinculo;

  useEffect(() => {
    if (!ehContaMp) return;
    setCarregandoMp(true);
    api.get<MpPagamentoEnviado[]>('/api/contas-pagar/mp-pagamentos?pendentes=1')
      .then((r) => {
        // O que veio da aba continua na lista mesmo se, entre um clique e outro, ja tiver titulo.
        const lista = mpPagamentoInicial && !r.some((p) => p.id === mpPagamentoInicial.id) ? [mpPagamentoInicial, ...r] : r;
        setPagamentosMp(lista);
      })
      .catch((e) => toast.erro(e.message))
      .finally(() => setCarregandoMp(false));
  }, [ehContaMp]);

  useEffect(() => {
    api.get<FormaPagamento[]>('/api/formas-pagamento')
      .then((r) => {
        const ativas = r.filter((f) => f.tipo !== 'crediario');
        setFormas(ativas);
        const last = localStorage.getItem(KEY_LAST_FP);
        const pixMp = mpPagamentoInicial ? ativas.find((f) => f.tipo === 'pix') : undefined;
        if (pixMp) {
          setForma(String(pixMp.id));
        } else if (last && ativas.some((f) => String(f.id) === last)) {
          setForma(last);
        } else {
          const pix = ativas.find((f) => /pix/i.test(f.nome));
          if (pix) setForma(String(pix.id));
        }
      })
      .catch((e) => toast.erro(e.message));
  }, []);

  // Quando a forma muda, recarrega os caixas que a aceitam.
  useEffect(() => {
    if (!forma) { setCaixas([]); setCaixa(''); return; }
    setCarregandoCaixas(true);
    // origem_saida=1: aqui o dinheiro SAI, entao o Cofre entra na lista quando a forma e dinheiro
    // (ele nao aceita forma nenhuma pra nao virar destino de venda). Ver [DA-427].
    api.get<Caixa[]>(`/api/caixa/contas-por-forma/${forma}?origem_saida=1`)
      .then((r) => {
        setCaixas(r);
        const last = localStorage.getItem(KEY_LAST_CAIXA);
        // Conta paga em ESPECIE sai do Cofre — decisao dele, 2026-09-05 ([DA-676]). O Cofre so
        // entra nesta lista quando a forma e' dinheiro, entao achar `eh_cofre` ja e' o teste da
        // forma. Vence o "ultimo usado" DE PROPOSITO: o habito gravado ali e' o Movimento de
        // Caixa, que e' justamente a segunda porta que este default existe pra fechar — enquanto
        // o localStorage ganhasse, o default novo nunca apareceria pra quem ja usou o modal.
        // Segue editavel; nas outras formas (pix/cartao) nada muda.
        const cofre = r.find((c) => c.eh_cofre);
        const contaMp = mpPagamentoInicial ? r.find((c) => ehContaMercadoPago(c.nome)) : undefined;
        if (contaMp) {
          setCaixa(String(contaMp.id));
        } else if (cofre) {
          setCaixa(String(cofre.id));
        } else if (last && r.some((c) => String(c.id) === last)) {
          setCaixa(last);
        } else if (r.length === 1) {
          setCaixa(String(r[0]!.id));
        } else {
          setCaixa('');
        }
      })
      .catch((e) => { toast.erro(e.message); setCaixas([]); })
      .finally(() => setCarregandoCaixas(false));
  }, [forma]);

  async function confirmar() {
    if (!forma) { toast.erro('Selecione a forma de pagamento'); return; }
    if (!caixa) { toast.erro('Selecione o caixa de origem'); return; }
    if (!valor || valor <= 0) { toast.erro('Informe o valor pago'); return; }
    if (faltaVinculoMp) { toast.erro('Escolha o pagamento do Mercado Pago ou marque que ele não aparece no MP'); return; }
    setSalvando(true);
    try {
      await api.post(`/api/contas-pagar/${conta.id}/baixar`, {
        forma_pagamento_id: Number(forma),
        caixa_id: Number(caixa),
        valor_pago: valor,
        observacoes: observacoes || undefined,
        mp_pagamento_enviado_id: ehContaMp && !semVinculo ? mpSel : null,
        mp_sem_vinculo: ehContaMp && semVinculo,
      });
      localStorage.setItem(KEY_LAST_FP, forma);
      localStorage.setItem(KEY_LAST_CAIXA, caixa);
      toast.sucesso('Conta baixada');
      onBaixado();
    } catch (e: any) {
      const saldo = lerSaldoInsuficiente(e);
      if (saldo) { setSaldoInsuf(saldo); return; }
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  // A recusa TOMA a tela no lugar do formulario — a conta NAO foi paga e isso precisa ser a unica
  // coisa visivel. Fechando o aviso o formulario volta com tudo preenchido (o componente nao
  // desmonta, so troca o que renderiza), pra corrigir o caixa ou o valor e tentar de novo.
  if (saldoInsuf) {
    return <SaldoInsuficienteModal info={saldoInsuf} onFechar={() => setSaldoInsuf(null)} />;
  }

  return (
    <Modal
      aberto={true}
      onFechar={onFechar}
      titulo="Baixar conta a pagar"
      largura={620}
      rodape={
        <>
          {faltaVinculoMp && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
              Escolha o pagamento do Mercado Pago, ou marque que ele não aparece.
            </span>
          )}
          <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
          <button type="button" className="btn-primario" disabled={salvando || faltaVinculoMp} onClick={() => void confirmar()}>
            {salvando ? 'Baixando...' : 'Confirmar pagamento'}
          </button>
        </>
      }
    >
      <div style={{ background: 'var(--card-bg)', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: 13 }}>
        <div><strong>{conta.descricao}</strong></div>
        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Documento <strong>{conta.numero_documento || '-'}</strong>
          {conta.parcela_total > 1 && <> · parcela {conta.parcela_numero}/{conta.parcela_total}</>}
          {' '} · Vencimento {formatDataSP(conta.data_vencimento)} · <span className="cel-num">{formatMoeda(conta.valor)}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Forma de pagamento *</label>
          <ComboBox
            opcoes={formas.map((f) => ({ value: f.id, label: f.nome }))}
            value={forma}
            onChange={(v) => setForma(String(v))}
            placeholder="Selecione..."
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Caixa de origem *</label>
          <ComboBox
            opcoes={caixas.map((c) => ({ value: c.id, label: c.nome }))}
            value={caixa}
            onChange={(v) => setCaixa(String(v))}
            placeholder={!forma ? 'Selecione a forma' : carregandoCaixas ? 'Carregando...' : caixas.length === 0 ? 'Nenhum caixa aceita' : 'Selecione...'}
            disabled={!forma || carregandoCaixas || caixas.length === 0}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Sera registrado como sangria (saida) neste caixa.
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Valor pago *</label>
          <MoneyInput className="cel-num" valor={valor} onChangeValor={setValor} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Observacoes</label>
          <input
            type="text"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {ehContaMp && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            Pagamento no Mercado Pago * <span style={{ fontWeight: 400 }}>(ainda sem título)</span>
          </div>
          <div style={{ border: '1px solid var(--input-border,#d1d5db)', borderRadius: 6, maxHeight: 210, overflowY: 'auto', opacity: semVinculo ? 0.5 : 1 }}>
            {carregandoMp && pagamentosMp.length === 0 ? (
              <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)' }}>Carregando...</div>
            ) : pagamentosMp.length === 0 ? (
              <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)' }}>Nenhum pagamento do Mercado Pago esperando título.</div>
            ) : pagamentosMp.map((p) => {
              const r = rotuloMpPagamento(p);
              const marcado = mpSel === p.id && !semVinculo;
              return (
                <label key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', fontSize: 13, cursor: semVinculo ? 'default' : 'pointer',
                  borderBottom: '1px solid var(--border-color,#e5e7eb)',
                  ...(marcado ? { backgroundColor: '#fde68a', color: '#000' } : {}),
                }}>
                  <input type="radio" name="mp-pagamento" disabled={semVinculo} checked={marcado} onChange={() => setMpSel(p.id)} />
                  <span style={{ width: 92 }}>{p.data_pagamento ? formatDataHoraSP(p.data_pagamento) : '—'}</span>
                  <span className="cel-num" style={{ width: 90, textAlign: 'right', fontWeight: 700 }}>{formatMoeda(Number(p.valor))}</span>
                  <span style={{ width: 100 }}>{r.tipo}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.detalhe}</span>
                </label>
              );
            })}
          </div>
          {mpSel && !semVinculo && (() => {
            const p = pagamentosMp.find((x) => x.id === mpSel);
            if (!p || Math.abs(Number(p.valor) - valor) < 0.005) return null;
            return (
              <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-muted)' }}>
                O pagamento foi de <span className="cel-num">{formatMoeda(Number(p.valor))}</span> e o valor pago aqui é <span className="cel-num">{formatMoeda(valor)}</span>.
              </div>
            );
          })()}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={semVinculo} onChange={(e) => setSemVinculo(e.target.checked)} />
            O pagamento não aparece no MP (fica anotado no título)
          </label>
        </div>
      )}
    </Modal>
  );
}
